import * as React from 'react';

import {
  enumOperationType,
  OperationType,
  PriceListType,
} from '@rematter/frontend-lib-api-client';
import {
  Button,
  Checkbox,
  ConsoleDialog,
  ContentSection,
  Radio,
  RadioGroupField,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { cn } from '@rematter/ui-web/lib';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucideDownload } from 'lucide-react';

import { customPriceListImportFromAtom } from '../../_graphql/mutationCustomPriceListLifecycle';
import { defaultPriceListImportFromAtom } from '../../_graphql/mutationDefaultPriceListOps';
import { customPriceListsListQueryAtom } from '../../_graphql/queryCustomPriceListsList';
import { defaultPriceListGetOrCreateMutationAtom } from '../../_graphql/queryDefaultPriceListGetOrCreate';
import { itemPricingsQueryAtom } from '../../_graphql/queryProductPricings';
import { useTranslations } from '../../i18n/useTranslations';

import { SelectCurrency } from '~/shared/components/select-fields/SelectCurrency';
import { SelectEnum } from '~/shared/components/select-fields/SelectEnum';
import { SelectFacility } from '~/shared/components/select-fields/SelectFacility';

type SourceType = 'default' | 'custom';

export type ImportedItemPricing = {
  id: string;
  itemId: string;
  itemName: string;
  pricingUnit: string;
  tiers: {
    maxAmount: number | null;
    minPriceFormula: string;
    targetPriceFormula: string;
    maxPriceFormula: string;
    scrapValue: number;
    position: number;
  }[];
};

interface ImportFromPriceListDialogProps {
  isOpen: boolean;
  onClose: () => void;
  targetCustomPriceListId?: string;
  targetDefaultPriceListId?: string;
  onSuccess: () => void;
  onStageImport?: (items: ImportedItemPricing[]) => void;
}

const $allCustomPriceLists = customPriceListsListQueryAtom(() => ({
  variables: {
    limit: 500,
    isArchived: false,
  },
}));

const $importSourcePriceListId = atom<string | null>(null);
const $importSourcePriceListType = atom<PriceListType | null>(null);

const $importSourceItems = itemPricingsQueryAtom((get) => ({
  variables: {
    priceListId: get($importSourcePriceListId) ?? '',
    priceListType: (get($importSourcePriceListType) ??
      'Custom') as PriceListType,
  },
  skip: !get($importSourcePriceListId) || !get($importSourcePriceListType),
}));

export const ImportFromPriceListDialog = ({
  isOpen,
  onClose,
  targetCustomPriceListId,
  targetDefaultPriceListId,
  onSuccess,
  onStageImport,
}: ImportFromPriceListDialogProps) => {
  const t = useTranslations();
  const toast = useToast();

  const [sourceType, setSourceType] = React.useState<SourceType>('default');
  const [facilityId, setFacilityId] = React.useState<string | undefined>(
    undefined,
  );
  const [operationType, setOperationType] = React.useState<string>('Purchase');
  const [currency, setCurrency] = React.useState('USD');
  const [selectedCustomPriceListId, setSelectedCustomPriceListId] =
    React.useState<string | undefined>(undefined);
  const [selectedItemPricingIds, setSelectedItemPricingIds] = React.useState<
    Set<string>
  >(new Set());
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const { data: allListsData } = useAtomValue($allCustomPriceLists);
  const getOrCreateDefaultPriceList = useSetAtom(
    defaultPriceListGetOrCreateMutationAtom(),
  );
  const importFromCustom = useSetAtom(customPriceListImportFromAtom({}));
  const importFromDefault = useSetAtom(defaultPriceListImportFromAtom({}));
  const isDefaultTarget = !!targetDefaultPriceListId;

  const [importSourceId, setImportSourceId] = useAtom($importSourcePriceListId);
  const setImportSourceType = useSetAtom($importSourcePriceListType);
  const { data: sourceItems, isFetching: sourceItemsFetching } =
    useAtomValue($importSourceItems);

  const availableCustomLists = React.useMemo(() => {
    const lists = allListsData?.customPriceLists ?? [];
    if (targetCustomPriceListId) {
      return lists.filter((l) => l.id !== targetCustomPriceListId);
    }
    return lists;
  }, [allListsData, targetCustomPriceListId]);

  React.useEffect(() => {
    if (sourceItems && sourceItems.length > 0) {
      setSelectedItemPricingIds(new Set(sourceItems.map((i) => i.id)));
    }
  }, [sourceItems]);

  const handleClose = React.useCallback(() => {
    setSourceType('default');
    setFacilityId(undefined);
    setOperationType('Purchase');
    setCurrency('USD');
    setSelectedCustomPriceListId(undefined);
    setSelectedItemPricingIds(new Set());
    setImportSourceId(null);
    setImportSourceType(null);
    onClose();
  }, [onClose, setImportSourceId, setImportSourceType]);

  const handleLoadSource = React.useCallback(async () => {
    if (sourceType === 'default') {
      if (!facilityId) {
        toast.error(t.pricing.customPriceLists.validation.facilityRequired);
        return;
      }
      try {
        const defaultPriceList = await getOrCreateDefaultPriceList({
          facilityId,
          operationType: operationType as OperationType,
          currency,
        });
        if (!defaultPriceList?.id) {
          toast.error(
            t.pricing.customPriceLists.toast.importFromError(
              'Default price list not found',
            ),
          );
          return;
        }
        setImportSourceType('Default' as PriceListType);
        setImportSourceId(defaultPriceList.id);
      } catch (err) {
        toast.error(
          t.pricing.customPriceLists.toast.importFromError(
            err instanceof Error ? err.message : 'Unknown error',
          ),
        );
      }
    } else {
      if (!selectedCustomPriceListId) {
        toast.error(
          t.pricing.customPriceLists.toast.importFromError(
            'Select a source price list',
          ),
        );
        return;
      }
      setImportSourceType('Custom' as PriceListType);
      setImportSourceId(selectedCustomPriceListId);
    }
  }, [
    sourceType,
    facilityId,
    operationType,
    currency,
    selectedCustomPriceListId,
    getOrCreateDefaultPriceList,
    setImportSourceId,
    setImportSourceType,
    toast,
    t,
  ]);

  const handleToggleItem = React.useCallback((itemId: string) => {
    setSelectedItemPricingIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  }, []);

  const handleToggleAll = React.useCallback(() => {
    const items = sourceItems ?? [];
    if (selectedItemPricingIds.size === items.length) {
      setSelectedItemPricingIds(new Set());
    } else {
      setSelectedItemPricingIds(new Set(items.map((i) => i.id)));
    }
  }, [selectedItemPricingIds, sourceItems]);

  const handleSubmit = React.useCallback(async () => {
    if (selectedItemPricingIds.size === 0) return;

    try {
      setIsSubmitting(true);

      if (onStageImport && sourceItems) {
        const selectedItems: ImportedItemPricing[] = sourceItems
          .filter((ip) => selectedItemPricingIds.has(ip.id))
          .map((ip) => ({
            id: ip.id,
            itemId: ip.item.id,
            itemName: ip.item.name,
            pricingUnit: ip.pricingUnit,
            tiers: ip.tiers.map((tier) => ({
              maxAmount: tier.maxAmount,
              minPriceFormula: tier.minPrice.formula,
              targetPriceFormula: tier.targetPrice.formula,
              maxPriceFormula: tier.maxPrice.formula,
              scrapValue: tier.scrapValue,
              position: tier.position,
            })),
          }));
        onStageImport(selectedItems);
        onSuccess();
        handleClose();
        return;
      }

      if (isDefaultTarget && targetDefaultPriceListId) {
        await importFromDefault({
          targetDefaultPriceListId,
          itemPricingIds: Array.from(selectedItemPricingIds),
        });
      } else if (!isDefaultTarget && targetCustomPriceListId) {
        await importFromCustom({
          targetCustomPriceListId,
          itemPricingIds: Array.from(selectedItemPricingIds),
        });
      }
      toast.success(t.pricing.customPriceLists.toast.importedFrom);
      onSuccess();
      handleClose();
    } catch (err) {
      toast.error(
        t.pricing.customPriceLists.toast.importFromError(
          err instanceof Error ? err.message : 'Unknown error',
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    selectedItemPricingIds,
    sourceItems,
    onStageImport,
    targetCustomPriceListId,
    targetDefaultPriceListId,
    isDefaultTarget,
    importFromCustom,
    importFromDefault,
    toast,
    t,
    onSuccess,
    handleClose,
  ]);

  const canSubmit = !isSubmitting && selectedItemPricingIds.size > 0;
  const hasLoadedSource = importSourceId !== null;
  const displayItems = sourceItems ?? [];

  return (
    <ConsoleDialog
      className="@ImportFromPriceListDialog"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      title={t.pricing.customPriceLists.detail.actions.importFrom}
      content={
        <div className="space-y-6">
          <RadioGroupField
            label={t.pricing.customPriceLists.copyFrom.sourceType}
            value={sourceType}
            onChange={(value) => {
              setSourceType(value as SourceType);
              setImportSourceId(null);
              setImportSourceType(null);
              setSelectedItemPricingIds(new Set());
            }}
            orientation="horizontal"
          >
            <Radio value="default">
              {t.pricing.customPriceLists.copyFrom.defaultPriceList}
            </Radio>
            <Radio value="custom">
              {t.pricing.customPriceLists.copyFrom.customPriceList}
            </Radio>
          </RadioGroupField>

          <ContentSection
            title={t.pricing.customPriceLists.importFrom.selectSource}
          >
            <div className="grid gap-4">
              {sourceType === 'default' && (
                <>
                  <SelectFacility
                    label={t.pricing.customPriceLists.create.fields.facility}
                    value={facilityId}
                    onChange={(value) => setFacilityId(value ?? undefined)}
                    placeholder={
                      t.pricing.customPriceLists.create.placeholders.facility
                    }
                    isRequired
                  />
                  <SelectEnum
                    enumValue={enumOperationType}
                    label={
                      t.pricing.customPriceLists.create.fields.operationType
                    }
                    value={operationType}
                    onChange={(value) => setOperationType(value ?? 'Purchase')}
                  />
                  <SelectCurrency
                    label={t.pricing.customPriceLists.create.fields.currency}
                    value={currency}
                    onChange={(value) => setCurrency(value ?? 'USD')}
                  />
                </>
              )}

              {sourceType === 'custom' && (
                <CustomPriceListSelect
                  lists={availableCustomLists}
                  value={selectedCustomPriceListId}
                  onChange={setSelectedCustomPriceListId}
                  label={t.pricing.customPriceLists.importFrom.sourcePriceList}
                />
              )}

              {!hasLoadedSource && (
                <Button
                  variant="outline"
                  onPress={handleLoadSource}
                  isDisabled={
                    sourceType === 'default'
                      ? !facilityId
                      : !selectedCustomPriceListId
                  }
                >
                  {t.pricing.customPriceLists.importFrom.selectItems}
                </Button>
              )}
            </div>
          </ContentSection>

          {hasLoadedSource && (
            <ContentSection
              title={t.pricing.customPriceLists.importFrom.selectItems}
            >
              {sourceItemsFetching ? (
                <div className="py-4 text-center text-sm text-neutral/50">
                  {t.common.loading.saving}
                </div>
              ) : displayItems.length === 0 ? (
                <div className="py-4 text-center text-sm text-neutral/50">
                  No items found in source price list
                </div>
              ) : (
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2 border-b border-neutral/10 pb-2">
                    <Checkbox
                      isSelected={
                        selectedItemPricingIds.size === displayItems.length &&
                        displayItems.length > 0
                      }
                      onChange={handleToggleAll}
                    >
                      {t.pricing.customPriceLists.importFrom.selectAll}
                    </Checkbox>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {displayItems.map((item) => {
                      const firstTier = item.tiers[0];
                      const targetFormula =
                        firstTier?.targetPrice?.formula ?? '';
                      const scrapValue = firstTier?.scrapValue;
                      const priceDisplay =
                        targetFormula || (scrapValue ? String(scrapValue) : '');

                      return (
                        <div
                          key={item.id}
                          className="flex items-center gap-2 px-1 py-1.5 text-sm hover:bg-neutral/5"
                        >
                          <Checkbox
                            isSelected={selectedItemPricingIds.has(item.id)}
                            onChange={() => handleToggleItem(item.id)}
                          >
                            <span className="truncate">{item.item.name}</span>
                          </Checkbox>
                          <span className="ml-auto flex items-center gap-3">
                            {priceDisplay && (
                              <span className="text-neutral/70 tabular-nums">
                                ${priceDisplay}
                              </span>
                            )}
                            <span className="w-16 text-right text-xs text-neutral/50">
                              {item.pricingUnit}
                            </span>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </ContentSection>
          )}
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            color="neutral"
            onPress={handleClose}
            isDisabled={isSubmitting}
          >
            {t.common.actions.cancel}
          </Button>
          <Button
            icon={<LucideDownload size={16} />}
            color="primary"
            onPress={handleSubmit}
            isDisabled={!canSubmit}
          >
            {isSubmitting
              ? t.common.loading.saving
              : t.pricing.customPriceLists.importFrom.importButton}
          </Button>
        </div>
      }
    />
  );
};

type CustomPriceListSelectProps = {
  lists: Array<{
    id: string;
    name: string;
    operationType: string;
    pricingMode: string;
  }>;
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  label: string;
};

const CustomPriceListSelect = ({
  lists,
  value,
  onChange,
  label,
}: CustomPriceListSelectProps) => {
  const [search, setSearch] = React.useState('');

  const filtered = React.useMemo(() => {
    if (!search.trim()) return lists;
    const term = search.toLowerCase();
    return lists.filter(
      (l) =>
        l.name.toLowerCase().includes(term) ||
        l.operationType.toLowerCase().includes(term),
    );
  }, [lists, search]);

  return (
    <div className="@CustomPriceListSelect flex flex-col gap-2">
      <label className="text-sm font-medium text-neutral/90">{label}</label>
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search..."
        className="rounded-md border border-neutral/20 px-3 py-2 text-sm outline-none focus:border-primary/50"
      />
      <div className="max-h-48 overflow-y-auto rounded-md border border-neutral/20">
        {filtered.length === 0 ? (
          <div className="px-3 py-4 text-center text-sm text-neutral/50">
            No price lists found
          </div>
        ) : (
          filtered.map((list) => (
            <button
              key={list.id}
              type="button"
              className={cn(
                'flex w-full items-center gap-2 border-b border-neutral/10 px-3 py-2 text-left text-sm last:border-b-0 hover:bg-neutral/5',
                value === list.id ? 'bg-primary/5 text-primary' : '',
              )}
              onClick={() => onChange(value === list.id ? undefined : list.id)}
            >
              <span className="flex-1 truncate">{list.name}</span>
              <span className="text-xs text-neutral/50">
                {list.operationType}
              </span>
            </button>
          ))
        )}
      </div>
    </div>
  );
};
